export const STATUS_TTL = 30_000;
export const STATUS_MAX_STALE = 5 * 60_000;

// Keep all status surfaces on one published snapshot. The incident dataset is
// small; request only the public fields rendered by the website.
export const STATUS_QUERY = `{
  "settings": *[_type == "websiteStatus" && _id == "websiteStatus"][0]{
    showBanner, message, severity, "incidentId": relatedIncident._ref
  },
  "incidents": *[_type == "incident"] | order(incidentDate desc, _id asc){
    "id": _id, title, incidentDate, desc,
    "status": coalesce(status[0]->name, status[0].name)
  }
}`;

export function statusUrl({
  projectId = "np7zertu",
  dataset = "production",
} = {}) {
  if (!/^[a-z0-9]+$/.test(projectId) || !/^[a-z0-9_-]+$/.test(dataset))
    throw new Error("Invalid Sanity status configuration");
  const url = new URL(
    `https://${projectId}.apicdn.sanity.io/v2025-02-19/data/query/${dataset}`,
  );
  url.searchParams.set("perspective", "published");
  url.searchParams.set("query", STATUS_QUERY);
  return url.toString();
}

export function normalizeStatus(result) {
  if (!result || !Array.isArray(result.incidents))
    throw new Error("Invalid status response");
  const settings = result.settings;
  if (
    settings != null &&
    (typeof settings.showBanner !== "boolean" ||
      !["informational", "degraded", "outage"].includes(settings.severity) ||
      (settings.showBanner &&
        (typeof settings.message !== "string" || !settings.message.trim())))
  )
    throw new Error("Invalid website status settings");
  return {
    settings: settings
      ? {
          showBanner: settings.showBanner,
          message:
            typeof settings.message === "string" ? settings.message.trim() : "",
          severity: settings.severity,
          incidentId:
            typeof settings.incidentId === "string"
              ? settings.incidentId
              : null,
        }
      : null,
    incidents: result.incidents.map((incident) => {
      if (
        !incident ||
        typeof incident.id !== "string" ||
        typeof incident.title !== "string"
      )
        throw new Error("Invalid incident");
      return {
        id: incident.id,
        title: incident.title,
        incidentDate:
          typeof incident.incidentDate === "string"
            ? incident.incidentDate
            : "",
        desc: typeof incident.desc === "string" ? incident.desc : "",
        status:
          typeof incident.status === "string" ? incident.status : "Update",
      };
    }),
  };
}

export function statusView(snapshot, now = Date.now()) {
  const age = now - snapshot.fetchedAt;
  // Allow the scheduled request to finish without flashing stale copy on every poll.
  const stale = Boolean(snapshot.error) || age >= STATUS_TTL + 10_000;
  const expired = age < 0 || age >= STATUS_MAX_STALE;
  const settings = !expired && snapshot.data?.settings;
  const available = Boolean(settings);
  return {
    available,
    stale,
    showBanner: available && settings.showBanner,
    severity: settings?.severity || "informational",
    message: !available
      ? "Status temporarily unavailable"
      : settings.showBanner
        ? settings.message
        : stale
          ? "Current status could not be confirmed"
          : "All systems operational",
    incidentId: settings?.incidentId,
  };
}

export function createStatusClient({
  url,
  fetcher = (...args) => fetch(...args),
  storage,
  now = Date.now,
}) {
  const key = `salesbinder-status-v1:${url}`;
  let snapshot = { data: null, fetchedAt: 0, error: null };
  let pending;
  let lastAttempt = -Infinity;
  try {
    const saved = JSON.parse(storage?.getItem(key) || "null");
    if (
      saved &&
      now() >= saved.fetchedAt &&
      now() - saved.fetchedAt < STATUS_MAX_STALE
    ) {
      snapshot = {
        data: normalizeStatus(saved.data),
        fetchedAt: saved.fetchedAt,
        error: null,
      };
    }
  } catch {
    /* Storage may be disabled or contain an older response shape. */
  }
  return {
    getSnapshot: () => snapshot,
    refresh() {
      if (pending) return pending;
      const age = now() - snapshot.fetchedAt;
      if (snapshot.data && age >= 0 && age < STATUS_TTL)
        return Promise.resolve(snapshot);
      if (now() - lastAttempt < STATUS_TTL) return Promise.resolve(snapshot);
      lastAttempt = now();
      pending = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8_000);
        try {
          // Revalidate the browser HTTP cache while retaining Sanity CDN caching.
          const response = await fetcher(url, {
            signal: controller.signal,
            credentials: "omit",
            cache: "no-cache",
          });
          if (!response.ok)
            throw new Error(`Status request failed (${response.status})`);
          const data = normalizeStatus((await response.json()).result);
          snapshot = { data, fetchedAt: now(), error: null };
          try {
            storage?.setItem(
              key,
              JSON.stringify({ data, fetchedAt: snapshot.fetchedAt }),
            );
          } catch {
            /* Optional cache. */
          }
        } catch {
          snapshot = { ...snapshot, error: "Status could not be refreshed" };
        } finally {
          clearTimeout(timeout);
        }
        return snapshot;
      })().finally(() => {
        pending = undefined;
      });
      return pending;
    },
  };
}
